/**
 * Pot Configurator Loader
 *
 * Liquid kan per collectie maar 50 producten renderen. De pot-collectie telt er
 * 300+, dus de duurdere potten vielen structureel buiten de configurator.
 *
 * Dit script haalt na horizontale scrollintentie een al gefilterde pagina op bij
 * het JSON-endpoint templates/product.pots-json.liquid
 * (?view=pots-json&page=N). Dat endpoint gebruikt `paginate ... by 250` en
 * dezelfde matching-regel als het Liquid-blok in sections/single-product.liquid.
 * Iedere scrollbatch voegt hooguit twaalf kaarten toe.
 *
 * Bijladen moet onzichtbaar zijn: de eerste scroll haalt de pagina al op
 * (zonder te tonen) en laadt de foto's van de volgende twaalf kaarten vooraf.
 * Die kaarten komen erbij zodra er nog ongeveer anderhalf scherm aan potten
 * over is, altijd áchteraan de rij: nooit tussen of vóór kaarten die al in
 * beeld zijn geweest, want dat laat de rij verspringen. Binnen een batch staan
 * ze op prijs. Bestaande potten blijven altijd zichtbaar; alleen als het
 * netwerk toch achterloopt staan er twee placeholder-kaarten achteraan.
 */

(function () {
  'use strict';

  var LOG = '[Pot Loader]';
  var BATCH_SIZE = 12;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    var slider = document.querySelector('.js-oppot-slider[data-min-diameter][data-max-diameter]');
    if (!slider) return;
    var container = slider.parentNode;
    var action = container && container.querySelector('[data-pot-loader-action]');
    if (!action) return;
    var status = container.querySelector('[data-pot-loader-status]');

    var state = createState(slider);
    scheduleMeasurement(slider, state, true);
    observeSliderSize(slider, state);
    action.hidden = true;
    slider.addEventListener(
      'scroll',
      function () {
        if (state.complete) return;
        // De eerste scroll is de intentie: haal de pagina alvast op.
        if (!state.prefetched) {
          state.prefetched = true;
          queueNextPage(state).catch(function () {});
        }
        if (state.loading || !isNearEnd(slider, state)) return;
        loadNextBatch(slider, action, status, state);
      },
      { passive: true }
    );
    action.addEventListener('click', function (event) {
      event.preventDefault();
      if (action.hidden) return;
      loadNextBatch(slider, action, status, state);
    });
  }

  function scheduleMeasurement(slider, state, measureCard) {
    if (state.measurementFrame) cancelAnimationFrame(state.measurementFrame);
    state.measurementFrame = requestAnimationFrame(function () {
      state.measurementFrame = null;
      state.clientWidth = slider.clientWidth;
      state.scrollWidth = slider.scrollWidth;
      if (!measureCard) return;
      var card = slider.querySelector('.related-pots');
      state.cardWidth = card ? card.getBoundingClientRect().width : 0;
    });
  }

  function observeSliderSize(slider, state) {
    if (typeof ResizeObserver === 'undefined') return;
    var observer = new ResizeObserver(function () {
      scheduleMeasurement(slider, state, true);
    });
    observer.observe(slider);
  }

  // Ruim voor het einde bijladen: binnen anderhalf scherm (minstens drie
  // kaarten) van het einde, zodat de nieuwe kaarten er al staan als je er bent.
  function isNearEnd(slider, state) {
    if (!state.cardWidth) return false;
    var remaining = state.scrollWidth - state.clientWidth - slider.scrollLeft;
    return remaining <= Math.max(state.cardWidth * 3, state.clientWidth * 1.5);
  }

  // Placeholders staan áchter de bestaande potten, in de rij zelf; ze dekken
  // nooit iets af. Je ziet ze alleen als je het einde haalt vóór het netwerk.
  function showLoading(slider, state) {
    slider.setAttribute('aria-busy', 'true');
    if (slider.querySelector('.pot-loading-slide')) return;
    for (var index = 0; index < 2; index += 1) {
      var placeholder = document.createElement('div');
      placeholder.className = 'pot-loading-slide pot-loading-skeleton__card';
      placeholder.setAttribute('aria-hidden', 'true');
      if (placeholder.style && state.cardWidth) placeholder.style.width = state.cardWidth + 'px';
      slider.appendChild(placeholder);
    }
  }

  function hideLoading(slider) {
    slider.removeAttribute('aria-busy');
    slider.querySelectorAll('.pot-loading-slide').forEach(function (placeholder) {
      placeholder.remove();
    });
  }

  function createState(slider) {
    var state = {
      groups: [],
      loading: false,
      complete: false,
      cardWidth: 0,
      clientWidth: 0,
      scrollWidth: 0,
      measurementFrame: null,
      prefetched: false,
      preloaded: {},
      pending: null,
      nextPage: 1,
      pages: null,
      renderedProducts: {},
      renderedVariants: {},
      renderedFamilies: {},
    };

    Array.prototype.forEach.call(slider.querySelectorAll('.related-pots'), function (card) {
      var input = card.querySelector('input');
      if (input) {
        state.renderedVariants[input.value] = true;
        state.renderedProducts[input.dataset.productId] = true;
      }
      var familyIds = (card.dataset.familyIds || '').split(',').map(Number).filter(Boolean);
      familyIds.forEach(function (id) {
        state.renderedProducts[id] = true;
      });
      // Zelfde sleutel als familyKey(): het laagste id van de volledige familielijst. De serverkaart
      // neemt alle familieleden in, in elke maat (true), net als pot_family_consumed in de section.
      if (familyIds.length > 1) state.renderedFamilies[String(Math.min.apply(null, familyIds))] = true;
    });

    return state;
  }

  async function loadNextBatch(slider, action, status, state) {
    if (state.loading || state.complete) return;

    state.loading = true;
    action.hidden = true;
    action.setAttribute('aria-disabled', 'true');
    setStatus(status, action.dataset.loadingLabel);
    showLoading(slider, state);

    var batch = [];
    try {
      while (batch.length < BATCH_SIZE) {
        if (state.groups.length) {
          batch.push(state.groups.shift());
          continue;
        }

        if (state.pages !== null && state.nextPage > state.pages) break;

        await queueNextPage(state);
      }

      render(slider, batch, state);
      state.complete = state.groups.length === 0 && state.pages !== null && state.nextPage > state.pages;
      action.hidden = true;
      action.textContent = action.dataset.retryLabel || action.textContent;
      setStatus(
        status,
        countLabel(state.complete ? action.dataset.completeLabel : action.dataset.addedLabel, batch.length)
      );
    } catch (error) {
      state.groups = batch.concat(state.groups);
      console.error(LOG, 'Laden mislukt:', error);
      action.hidden = false;
      action.textContent = action.dataset.retryLabel || action.dataset.actionLabel || action.textContent;
      setStatus(status, action.dataset.errorLabel);
    } finally {
      state.loading = false;
      action.removeAttribute('aria-disabled');
      hideLoading(slider);
    }
  }

  // Eén request tegelijk: de prefetch van de eerste scroll en een batch die
  // erop wacht delen dezelfde fetch.
  function queueNextPage(state) {
    if (state.pending) return state.pending;
    if (state.pages !== null && state.nextPage > state.pages) return Promise.resolve();

    state.pending = fetchPage(state.nextPage)
      .then(function (result) {
        state.nextPage += 1;
        state.pages = Math.max(0, Number(result.pages || 0));
        queuePage(state, result.items);
        preloadNext(state);

        if ((result.collections_total || 1) > 1) {
          console.warn(LOG, 'Plant verwijst naar meer dan een pot-collectie; alleen de eerste wordt volledig geladen.');
        }
      })
      .finally(function () {
        state.pending = null;
      });
    return state.pending;
  }

  // Foto's van de eerstvolgende batch alvast in de browsercache, zodat een
  // nieuwe kaart meteen met foto verschijnt in plaats van eerst leeg.
  function preloadNext(state) {
    if (typeof Image === 'undefined') return;
    state.groups.slice(0, BATCH_SIZE).forEach(function (group) {
      var url = group[0] && group[0].image_200;
      if (!url || state.preloaded[url]) return;
      state.preloaded[url] = true;
      new Image().src = encodeURI(url);
    });
  }

  function setStatus(status, message) {
    if (status) status.textContent = message || '';
  }

  function countLabel(template, count) {
    return String(template || '').replace('__COUNT__', String(count));
  }

  function fetchPage(page) {
    var url = window.location.pathname + '?view=pots-json&page=' + page;

    // Geen Accept: application/json meesturen. Shopify geeft dan de standaard
    // product-JSON terug in plaats van de alternatieve template.
    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var data = JSON.parse(text);
        if (!data || !Array.isArray(data.items)) {
          throw new Error('Onverwacht antwoord van ?view=pots-json');
        }
        return data;
      });
  }

  function queuePage(state, items) {
    var fresh = items.filter(function (item) {
      if (state.renderedVariants[item.variant_id] || state.renderedProducts[item.product_id]) return false;
      // Een model heeft één kaart: een kleur in een andere maat dan de bestaande familiekaart krijgt
      // geen eigen kaart. Dezelfde maat wel, want die staat niet op die kaart en blijft zo kiesbaar.
      var key = familyKey(item);
      var cardSize = key ? state.renderedFamilies[key] : null;
      if (cardSize === true || (cardSize && cardSize !== item.variant_title)) return false;
      state.renderedVariants[item.variant_id] = true;
      state.renderedProducts[item.product_id] = true;
      return true;
    });

    if (!fresh.length) return;

    var groups = groupByFamily(fresh);
    groups.forEach(function (group) {
      group.sort(function (a, b) {
        return Number(a.price) - Number(b.price);
      });
      // Alleen een kaart met kleurbolletjes (zie render) neemt de familie in, met de maat van haar anker.
      var key = familyKey(group[0]);
      if (!key || state.renderedFamilies[key]) return;
      var anchorSize = group[0].variant_title;
      var sameSize = group.filter(function (item) {
        return item.variant_title === anchorSize;
      });
      if (sameSize.length > 1) state.renderedFamilies[key] = anchorSize;
    });
    groups.sort(function (a, b) {
      return Number(a[0].price) - Number(b[0].price);
    });
    state.groups = state.groups.concat(groups);
  }

  function render(slider, groups, state) {
    if (!groups.length) return;

    var labels = {
      familySummary: slider.dataset.familySummaryTemplate || '__COUNT__',
      quickview: slider.dataset.quickviewTemplate || '__COLOR__',
      selectColor: slider.dataset.selectColorTemplate || '__COLOR__',
    };

    groups.forEach(function (group) {
      // Kleurwissel behoudt de maat: alleen kleuren met exact dezelfde
      // variant-titel (D x H) als de anker-kaart krijgen een bolletje.
      var anchor = group[0];
      var sameSize = group.filter(function (item) {
        return item.variant_title === anchor.variant_title;
      });
      slider.appendChild(createPotElement(anchor, sameSize.length > 1 ? sameSize : null, labels));
    });

    hideLoading(slider);
    scheduleMeasurement(slider, state, false);
    preloadNext(state);

    // De voortgangsbalk berekent zijn breedte kort na DOMContentLoaded, dus voor
    // deze fetch klaar is. Een scroll-event laat hem opnieuw rekenen.
    slider.dispatchEvent(new Event('scroll'));
  }

  /**
   * Groepeer items van dezelfde potkleur-familie tot een kaart. Items zonder
   * kleurnaam, zonder hex of zonder familie blijven een eigen kaart - fail-open,
   * exact zoals voor de kleurbolletjes.
   */
  function groupByFamily(items) {
    var groups = [];
    var byKey = {};

    items.forEach(function (item) {
      var key = familyKey(item);
      if (!key) {
        groups.push([item]);
        return;
      }
      if (!byKey[key]) {
        byKey[key] = [];
        groups.push(byKey[key]);
      }
      byKey[key].push(item);
    });

    return groups;
  }

  function familyKey(item) {
    if (!item.color_name || !item.color_hex) return null;
    var ids = (item.family_ids || []).map(Number).filter(Boolean);
    if (ids.length < 2) return null;
    // Elk familielid draagt dezelfde lijst, dus het laagste id is een stabiele sleutel.
    return String(Math.min.apply(null, ids));
  }

  function createPotElement(item, family, labels) {
    var priceParts = String(item.price_formatted || '').split(',');
    var potDiameter = item.pot_diameter || 0;
    var hasFamily = Boolean(family && window.PotColorSwatches);
    var potFamilyId = hasFamily
      ? String(
          Math.min.apply(
            null,
            family.map(function (member) {
              return Number(member.product_id);
            })
          )
        )
      : '';

    var div = document.createElement('div');
    div.className = 'related-pots rounded-2 position-relative js-lazy-loaded' + (hasFamily ? ' related-pots--family' : '');
    if (hasFamily) {
      div.dataset.potFamilyId = potFamilyId;
      div.dataset.familyIds = family
        .map(function (member) {
          return member.product_id;
        })
        .join(',');
    }
    // data-order 0: het JSON-endpoint pagineert de vaste potcollectie van de plant.
    div.dataset.order = 0;
    div.dataset.suborder = item.price;

    div.innerHTML =
      '<input class="related-pots__input" type="radio" name="addExtraPot"' +
      ' id="pot-' + item.variant_id + '"' +
      ' data-diameter="' + (potDiameter || '') + '"' +
      ' data-product-id="' + item.product_id + '"' +
      ' data-pot-price="' + item.price + '"' +
      ' data-pot-compare-price="' + (item.compare_at_price || 0) + '"' +
      ' value="' + item.variant_id + '">' +
      '<label class="js-select-pot related-pots__label" for="pot-' + item.variant_id + '"' +
      (keyboardCards()
        ? ' tabindex="0" role="button" aria-pressed="false"' +
          ' aria-label="' + escapeHtml(item.title + ' – ' + item.variant_title + ', ' + (item.price_formatted ? ((window.FLORAFY_PDP && window.FLORAFY_PDP.currencySymbol) || '€') + item.price_formatted : '')) + '"'
        : '') +
      '>' +
      '<span class="related-pots__price d-flex align-items-start">' +
      '<span class="lh-1 fw-medium">' + escapeHtml(priceParts[0]) + ',</span>' +
      '<span class="lh-1 fw-medium">' + escapeHtml(priceParts[1] || '') + '</span>' +
      '</span>' +
      '<img class="img-abs-center" src="' + encodeURI(item.image_200 || '') + '"' +
      ' alt="' + escapeHtml(item.image_alt || item.title) + '"' +
      ' width="100" height="100" loading="eager" decoding="async">' +
      (potDiameter
        ? '<span class="related-pots__diameter">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><line x1="5" y1="12" x2="19" y2="12"/><line x1="5" y1="11" x2="5" y2="13"/><line x1="19" y1="11" x2="19" y2="13"/></svg>' +
          potDiameter + 'cm</span>'
        : '') +
      '</label>' +
      '<button type="button" class="ui-icon-button ui-icon-button--glass pdp-pot-quickview js-quickview-toggle"' +
      ' aria-label="' + escapeHtml(labels.quickview.replace('__COLOR__', item.title)) + '"' +
      ' data-title="' + escapeHtml(item.title + ' - ' + item.variant_title) + '"' +
      ' data-variant-title="' + escapeHtml(item.variant_title) + '"' +
      ' data-price="' + item.price + '"' +
      ' data-description="' + escapeHtml(item.description || '') + '"' +
      ' data-url="' + encodeURI(item.url) + '?variant=' + item.variant_id + '"' +
      ' data-image="' + encodeURI(item.image_600 || '') + '"' +
      ' data-bs-toggle="modal" data-bs-target="#exampleModal">' +
      '<i class="ui-icon-button__icon fa-sharp fa-solid fa-magnifying-glass" aria-hidden="true"></i>' +
      '</button>' +
      (hasFamily
        ? window.PotColorSwatches.buildFamilyCardHtml(family, labels, potFamilyId)
        : '');

    return div;
  }

  // Pot cards are keyboard buttons only where pot-color-swatches.js handles Enter,
  // Space and aria-pressed (single-product.liquid). The tier template does not
  // load it, so its appended cards stay plain labels like its server cards.
  function keyboardCards() {
    return Boolean(document.querySelector('label.js-select-pot[role="button"]'));
  }

  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text == null ? '' : text;
    return div.innerHTML.replace(/"/g, '&quot;');
  }
})();
